import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, Loader2, Users } from "lucide-react";
import api from "@/lib/api";

const AREA_PARAM = { SOCIAL: "SOCIAL", STILLTOUCHEDX: "STX", MARKETPLACE: "MP" };

/**
 * FollowListModal — zeigt "Follower" bzw. "Folgt" des EIGENEN Profils, pro Bereich.
 * type: "followers" | "following"; area: "SOCIAL" | "STILLTOUCHEDX" | "MARKETPLACE".
 */
export default function FollowListModal({ open, type, area, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const areaParam = AREA_PARAM[area] || "SOCIAL";
      const path = type === "followers" ? "followers" : "following";
      const { data } = await api.get(`/me/${path}?area=${areaParam}`);
      setRows(data[path] || data.follows || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [type, area]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;
  const title = type === "followers"
    ? t("public_profile.followers", { defaultValue: "Follower" })
    : t("public_profile.following", { defaultValue: "Folgt" });

  return (
    <div data-testid="follow-list-modal" className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#EEE]">
          <h3 className="text-sm font-bold text-[#0A0A0A]">{title}</h3>
          <button data-testid="follow-list-close" onClick={onClose} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#999]" /></div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-[#999]">
              <Users size={28} className="mx-auto text-[#DDD] mb-2" />
              {type === "followers"
                ? t("follow_list.no_followers", { defaultValue: "Noch keine Follower." })
                : t("follow_list.no_following", { defaultValue: "Du folgst noch niemandem." })}
            </div>
          ) : (
            <ul className="divide-y divide-[#F2F2F2]">
              {rows.map((r) => (
                <li key={r.profile_id}>
                  <button
                    data-testid={`follow-row-${r.profile_id}`}
                    onClick={() => { if (r.handle) { onClose(); navigate(`/user/${r.handle}`); } }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAFA] transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#F0F0F0] overflow-hidden flex items-center justify-center shrink-0">
                      {r.avatar_url ? <img src={r.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="text-sm font-bold text-[#999]">{(r.display_name || r.handle || "?")[0]?.toUpperCase()}</span>}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#0A0A0A] truncate">{r.display_name || r.handle}</p>
                      {r.handle && <p className="text-xs text-[#999] truncate">@{r.handle}</p>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
